import { useEffect, useState } from "react";
import { ConfirmDialog, Input } from "../../ui";
import "./styles.scss";

type DeleteTournamentDialogProps = {
  tournamentName: string;
  isOpen: boolean;
  onDelete: () => Promise<void>;
  onClose: (update?: boolean) => void;
};

// Normalize spaces: trim and replace multiple spaces with single space
const normalizeSpaces = (text: string): string => {
  return text.trim().replace(/\s+/g, " ");
};

const DeleteTournamentDialog = (props: DeleteTournamentDialogProps) => {
  const [confirmationText, setConfirmationText] = useState("");

  // Get normalized tournament name for display
  const normalizedTournamentName = normalizeSpaces(props.tournamentName);

  useEffect(() => {
    if (props.isOpen) {
      setConfirmationText("");
    }
  }, [props.isOpen]);

  // Errors are reported by the parent; a throw keeps the dialog open.
  const handleDelete = async () => {
    const normalizedConfirmation = normalizeSpaces(confirmationText);
    const normalizedName = normalizeSpaces(props.tournamentName);

    if (normalizedConfirmation !== normalizedName) throw new Error("Tournament name does not match");

    await props.onDelete();
  };

  const isDeleteDisabled = normalizeSpaces(confirmationText) !== normalizedTournamentName;

  return (
    <ConfirmDialog
      open={props.isOpen}
      onClose={(confirmed) => {
        setConfirmationText("");
        props.onClose(confirmed ? true : undefined);
      }}
      onConfirm={handleDelete}
      title="Are you sure?"
      confirmLabel="Delete"
      confirmDisabled={isDeleteDisabled}
      message={
        <>
          <p>
            This action cannot be undone. If you delete "{normalizedTournamentName}", you will lose all its data including
            categories, teams, and matches.
          </p>
          <p>
            To confirm deletion, please type the tournament name: <strong>"{normalizedTournamentName}"</strong>
          </p>
        </>
      }
    >
      <Input
        className="delete-tournament-dialog__input"
        type="text"
        value={confirmationText}
        onChange={(e) => setConfirmationText(e.target.value)}
        onBlur={(e) => setConfirmationText(e.target.value)}
        placeholder="Type tournament name"
        aria-label="Type tournament name"
        autoFocus
      />
    </ConfirmDialog>
  );
};

export default DeleteTournamentDialog;
